import React, { useState, useEffect } from "react";
import "./case-paper.css";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiList,
  FiPlus,
  FiMinus,
  FiDroplet,
  FiHeart,
  FiThermometer,
  FiActivity,
  FiX,
  FiWind,
} from "react-icons/fi";
import { FaBalanceScale, FaRulerVertical } from "react-icons/fa";
import SkeletonLoader from "../../resuableComponents/SkeletonLoader";
import { isBranchAccessError } from "../../../utils/errorHandler";

const CasePaper = ({ patientId }) => {
  const patientTypeLabels = {
    regular: "Regular",
    demo: "Demo",
    new: "New",
  };
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [error, setError] = useState(null);
  const itemsPerPage = 5;

  const authData = JSON.parse(localStorage.getItem("authData"));

  const formatDate1 = (dateStr) => {
    if (!dateStr) return "N/A";
    const [datePart, timePart] = dateStr.split(" ");
    const [day, month, year] = datePart.split("-");
    const isoString = `${year}-${month}-${day}T${timePart}`;
    return dayjs(isoString).format("DD/MM/YYYY");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  useEffect(() => {
    const fetchConsultations = async () => {
      if (!patientId) return;
      if (!authData?.accessToken) return;

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}patient-history?patientId=${patientId}`,
          {
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        const consultations = response.data.history
          .filter((item) => item.consultation)
          .map((item) => {
            const consultation = item.consultation;
            let visits = consultation.visits ? [...consultation.visits] : [];

            if (visits.length === 0) {
              visits.push({
                date: consultation.createdAt,
                time: new Date(consultation.createdAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
                doctor: consultation.doctor,
                observations: consultation.observations || {},
                complaints: consultation.complaints || "",
                diagnosis: consultation.diagnosis || "",
                notes: consultation.notes || "",
                treatments: consultation.treatments || [],
                prescribedMedicines: consultation.prescribedMedicines || [],
                followUpDate: consultation.followUpDate,
              });
            }

            visits = visits.map((visit) => {
              const pkg = (consultation.packages || []).find(
                (item) =>
                  item.duration === "multi" &&
                  String(item.billingVisitId) !== String(visit._id) &&
                  (item.sessions || []).some((session) => String(session.visitId) === String(visit._id))
              );
              if (!pkg) return visit;
              return {
                ...visit,
                packageTitle: pkg.kind === "custom" ? (pkg.packageName || "Custom package") : "this package",
                packageTreatments: (pkg.items || []).map((item) => ({
                  name: item.name,
                  charged: pkg.kind !== "custom" && item.name === pkg.masterName && !item.waived,
                })),
              };
            });

            return {
              appointmentId: item.appointmentId,
              appointmentDate: item.appointmentDate,
              status: item.status,
              visits,
              visitCount: consultation.visitCount || visits.length || 0,
              consultationId: consultation._id,
              invoiceId: consultation.invoiceId,
              patientType: consultation.patientType,
            };
          });

        setConsultations(consultations);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch consultations:", err);
        if (isBranchAccessError(err)) {
          setError("You don't have access to this patient's data. This may be due to branch restrictions.");
          toast.error("🔒 Access Denied: Unable to load patient history", { autoClose: 7000 });
        } else {
          setConsultations([]);
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConsultations();
  }, [patientId]);

  const toggleRowExpand = (index) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const totalPages = Math.ceil(consultations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentConsultations = consultations.slice(startIndex, startIndex + itemsPerPage);

  const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

  // Vitals are under observations.oe in the API
  const VitalsDisplay = ({ observations }) => {
    const oe = observations?.oe || {};
    return (
      <div className="cp-vitals-compact">
        <div className="cp-vital-item" title="Blood Pressure">
          <FiActivity className="cp-vital-icon" />
          <span>{oe.bp || "—"}</span>
        </div>
        <div className="cp-vital-item" title="Pulse">
          <FiHeart className="cp-vital-icon" />
          <span>{oe.pulse || "—"}</span>
        </div>
        <div className="cp-vital-item" title="Temperature">
          <FiThermometer className="cp-vital-icon" />
          <span>{oe.temp || "—"}</span>
        </div>
        <div className="cp-vital-item" title="Weight">
          <FaBalanceScale className="cp-vital-icon" />
          <span>{oe.weight || "—"}</span>
        </div>
        <div className="cp-vital-item" title="Height">
          <FaRulerVertical className="cp-vital-icon" />
          <span>{oe.height || "—"}</span>
        </div>
        <div className="cp-vital-item" title="SpO2">
          <FiWind className="cp-vital-icon" />
          <span>{oe.spo2 || "—"}</span>
        </div>
        <div className="cp-vital-item" title="Blood Sugar Level">
          <FiDroplet className="cp-vital-icon" />
          <span>{observations?.bsl || "—"}</span>
        </div>
      </div>
    );
  };

  // Modal showing full visit details
  const VisitModal = ({ visit, onClose }) => {
    if (!visit) return null;
    const oe = visit.observations?.oe || {};
    const se = visit.observations?.se || {};
    const exam = visit.observations?.examination || {};
    const mh = visit.observations?.menstrualHistory || {};
    const doctorName = typeof visit.doctor === "object" ? visit.doctor?.name : visit.doctor;

    const Field = ({ label, value }) =>
      value ? (
        <div className="cp-detail-field">
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ) : null;

    const Section = ({ title, children, show }) =>
      show ? (
        <section className="cp-detail-section">
          <h4>{title}</h4>
          {children}
        </section>
      ) : null;

    return (
      <div className="cp-modal-overlay" onClick={onClose}>
        <div className="cp-modal cp-detail-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="cp-modal-header">
            <div>
              <p className="cp-detail-kicker">Visit details</p>
              <h3>{dayjs(visit.date).format("DD MMM YYYY")}</h3>
              <div className="cp-detail-meta">
                {visit.time && <span>{visit.time}</span>}
                {doctorName && <span>Dr. {doctorName}</span>}
              </div>
            </div>
            <button type="button" className="cp-modal-close" onClick={onClose} aria-label="Close">
              <FiX />
            </button>
          </div>

          <div className="cp-modal-body">
            <Section title="Vitals" show={Object.values(oe).some(Boolean) || visit.observations?.bsl}>
              <div className="cp-detail-vitals">
                <Field label="Blood pressure" value={oe.bp} />
                <Field label="Pulse" value={oe.pulse} />
                <Field label="Temperature" value={oe.temp} />
                <Field label="Weight" value={oe.weight ? `${oe.weight} kg` : ""} />
                <Field label="Height" value={oe.height ? `${oe.height} cm` : ""} />
                <Field label="SpO2" value={oe.spo2 ? `${oe.spo2}%` : ""} />
                <Field label="Blood sugar" value={visit.observations?.bsl} />
              </div>
            </Section>

            <Section title="Complaints and diagnosis" show={visit.complaints || visit.diagnosis}>
              <div className="cp-detail-grid">
                <Field label="Complaints" value={visit.complaints} />
                <Field label="Diagnosis" value={visit.diagnosis} />
              </div>
            </Section>

            <Section title="Systemic examination" show={Object.values(se).some(Boolean)}>
              <div className="cp-detail-grid">
                <Field label="CVS" value={se.cvs} />
                <Field label="CNS" value={se.cns} />
                <Field label="RS" value={se.rs} />
                <Field label="P/A" value={se.pa} />
              </div>
            </Section>

            <Section title="Ayurvedic examination" show={Object.values(exam).some(Boolean)}>
              <div className="cp-detail-grid">
                <Field label="Mala (bowel)" value={exam.mala} />
                <Field label="Mutra (urine)" value={exam.mutra} />
                <Field label="Jivha (tongue)" value={exam.jivha} />
                <Field label="Nidra (sleep)" value={exam.nidra} />
              </div>
            </Section>

            <Section title="Lifestyle" show={visit.observations?.dh || visit.observations?.addictions}>
              <div className="cp-detail-grid">
                <Field label="Dietary history" value={visit.observations?.dh} />
                <Field label="Addictions" value={visit.observations?.addictions} />
              </div>
            </Section>

            <Section title="Menstrual history" show={Object.values(mh).some(Boolean)}>
              <div className="cp-detail-grid">
                <Field label="Cycle regularity" value={mh.cycleRegularity} />
                <Field label="Flow" value={mh.flow} />
                <Field label="Clots" value={mh.clots} />
                <Field label="Dysmenorrhea" value={mh.dysmenorrhea} />
                <Field label="Menopause" value={mh.menopause} />
                <Field label="Menarche" value={mh.menarche} />
                <Field label="LMP" value={mh.lmp} />
                <Field label="Bleeding duration" value={mh.bleedingDuration} />
                <Field label="Cycle duration" value={mh.cycleDuration} />
              </div>
            </Section>

            <Section title="Package treatments" show={visit.packageTreatments?.length > 0}>
              <p className="cp-package-note">
                Included in {visit.packageTitle || "this package"} on this appointment. The price was set on the first visit.
              </p>
              <div className="cp-detail-list">
                {(visit.packageTreatments || []).map((treatment, index) => (
                  <div className="cp-detail-row" key={`${treatment.name}-${index}`}>
                    <span>{treatment.name}</span>
                    <em>{treatment.charged ? "Charged on the first visit" : "Included · ₹0"}</em>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Treatments" show={visit.treatments?.length > 0}>
              <div className="cp-detail-list">
                {(visit.treatments || []).map((treatment, index) => {
                  const name = typeof treatment === "object" ? treatment.name || "Treatment" : treatment;
                  const cost = typeof treatment === "object" ? treatment.cost : null;
                  return (
                    <div className="cp-detail-row" key={`${name}-${index}`}>
                      <span>{name}</span>
                      <em>{cost || cost === 0 ? `₹${cost}` : ""}</em>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Medicines" show={visit.prescribedMedicines?.length > 0}>
              <div className="cp-detail-list">
                {(visit.prescribedMedicines || []).map((medicine, index) => (
                  <div className="cp-detail-row" key={`${medicine.name || "med"}-${index}`}>
                    <span>
                      {medicine.name || "Medicine"}
                      {medicine.dosage ? <small>{medicine.dosage}</small> : null}
                    </span>
                    <em>{medicine.cost ? `₹${medicine.cost}` : ""}</em>
                  </div>
                ))}
              </div>
            </Section>

            {visit.followUpDate && (
              <div className="cp-detail-followup">
                <span>Follow-up</span>
                <strong>{formatDate(visit.followUpDate)}</strong>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <SkeletonLoader variant="case-paper" count={3} />;

  if (error) {
    return (
      <div className="cp-error-container">
        <div className="cp-error-banner">
          <span>🔒</span>
          <span>{error}</span>
          <button className="cp-error-close" onClick={() => setError(null)}>×</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-container">
      <div className="cp-header">
        <h2 className="cp-title">Consultation History</h2>
        <div className="cp-view-toggle">
          <button
            className={`cp-view-button ${viewMode === "table" ? "active" : ""}`}
            onClick={() => setViewMode("table")}
            title="List view"
          >
            <FiList />
          </button>
          <button
            className={`cp-view-button ${viewMode === "card" ? "active" : ""}`}
            onClick={() => setViewMode("card")}
            title="Card view"
          >
            <FiGrid />
          </button>
        </div>
      </div>

      {consultations.length === 0 ? (
        <div className="cp-empty-state">
          <div className="cp-empty-icon">📋</div>
          <h3>No consultations yet</h3>
          <p>Consultation history will appear here once appointments are completed.</p>
        </div>
      ) : (
        <>
          {viewMode === "table" ? (
            <div className="cp-table-container">
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Appointment Date</th>
                    <th>Patient Type</th>
                    <th>Visit Count</th>
                    <th>Latest Complaints</th>
                    <th>Status</th>
                    <th>Show Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {currentConsultations.map((c, index) => {
                    const globalIndex = startIndex + index;
                    const latestVisit = c.visits[c.visits.length - 1];
                    return (
                      <React.Fragment key={c.appointmentId}>
                        <tr>
                          <td>{formatDate1(c.appointmentDate)}</td>
                          <td>{patientTypeLabels[c.patientType] || "N/A"}</td>
                          <td>{c.visitCount} visits</td>
                          <td className="cp-truncate-cell">
                            <div className="cp-truncate-content">
                              {latestVisit?.complaints || "N/A"}
                            </div>
                          </td>
                          <td>{c.status || "N/A"}</td>
                          <td>
                            <button
                              className="cp-expand-button"
                              onClick={() => toggleRowExpand(globalIndex)}
                              title={expandedRows[globalIndex] ? "Hide Visits" : "Show Visits"}
                            >
                              {expandedRows[globalIndex] ? <FiMinus /> : <FiPlus />}
                            </button>
                          </td>
                        </tr>

                        {expandedRows[globalIndex] && (
                          <tr>
                            <td colSpan="6" className="cp-expanded-row">
                              <div className="cp-visits-timeline">
                                <h4>Visits Timeline</h4>
                                {c.visits.map((visit, visitIndex) => (
                                  <div key={visit._id || visitIndex} className="cp-visit-card">
                                    <div className="cp-visit-header">
                                      <span className="cp-visit-date">
                                        {dayjs(visit.date).format("DD MMM YYYY")} at {visit.time}
                                      </span>
                                      <span className="cp-visit-doctor">
                                        Dr. {typeof visit.doctor === "object" ? visit.doctor.name : visit.doctor}
                                      </span>
                                    </div>

                                    <div className="cp-visit-body">
                                      <div className="cp-visit-section">
                                        <h5>Vitals</h5>
                                        <VitalsDisplay observations={visit.observations} />
                                      </div>
                                      {visit.packageTreatments?.length > 0 && (
                                        <div className="cp-visit-section">
                                          <h5>Package treatments</h5>
                                          <p>{visit.packageTreatments.map((treatment) => treatment.name).join(", ")}</p>
                                        </div>
                                      )}
                                    </div>

                                    <button
                                      className="cp-visit-toggle-btn"
                                      onClick={() => setSelectedVisit(visit)}
                                    >
                                      View More
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="cp-card-view">
              {currentConsultations.flatMap((c) =>
                c.visits.map((visit, visitIndex) => (
                  <div key={visit._id || `${c.appointmentId}-${visitIndex}`} className="cp-visit-standalone-card">
                    <div className="cp-visit-standalone-meta">
                      <span>Appt: {formatDate1(c.appointmentDate)}</span>
                      <span>{patientTypeLabels[c.patientType] || "N/A"}</span>
                      <span className={`cp-status-badge cp-status-${c.status?.toLowerCase()}`}>
                        {c.status || "N/A"}
                      </span>
                    </div>

                    <div className="cp-visit-standalone-header">
                      <span className="cp-visit-date">
                        {dayjs(visit.date).format("DD MMM YYYY")} at {visit.time}
                      </span>
                      <span className="cp-visit-doctor">
                        Dr. {typeof visit.doctor === "object" ? visit.doctor.name : visit.doctor}
                      </span>
                    </div>

                    <div className="cp-visit-standalone-vitals">
                      <VitalsDisplay observations={visit.observations} />
                    </div>

                    <div className="cp-visit-standalone-summary">
                      {visit.complaints && (
                        <p><strong>Complaints:</strong> {visit.complaints}</p>
                      )}
                      {visit.diagnosis && (
                        <p><strong>Diagnosis:</strong> {visit.diagnosis}</p>
                      )}
                      {visit.packageTreatments?.length > 0 && (
                        <p><strong>Package treatments:</strong> {visit.packageTreatments.map((treatment) => treatment.name).join(", ")}</p>
                      )}
                    </div>

                    <button
                      className="cp-visit-toggle-btn"
                      onClick={() => setSelectedVisit(visit)}
                    >
                      View More
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {totalPages > 1 && (
            <div className="cp-pagination">
              <button
                type="button"
                className="cp-pagination-button"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
              <div className="cp-pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    type="button"
                    key={page}
                    className={`cp-pagination-number ${currentPage === page ? "active" : ""}`}
                    onClick={() => goToPage(page)}
                    aria-current={currentPage === page ? "page" : undefined}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="cp-pagination-button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                
                <FiChevronRight />
              </button>
            </div>
          )}
        </>
      )}

      {/* Visit detail modal */}
      {selectedVisit && (
        <VisitModal visit={selectedVisit} onClose={() => setSelectedVisit(null)} />
      )}
    </div>
  );
};

export default CasePaper;
